import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Card, Col, Layout, Row, Typography } from "antd";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  SendOutlined,
  InboxOutlined,
  ExperimentOutlined,
  ReconciliationOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";

import CustomHeader from "../Header";
import Footer from "../Footer";
import "./JobWorkFeature.css";
import { scrollToElementById } from "../../../Utility/scrollUtils";
import { Helmet } from "react-helmet-async";

const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;

// --- TEXT CONTENT ---
enum JobWorkText {
  HERO_TITLE = "Job Work Management with Ease Margin",
  HERO_DESCRIPTION = `Managing job work shouldn’t be complicated. From issuing service orders to tracking material movement, receiving finished goods, and reconciling invoices — Ease Margin brings everything into one simple, powerful SaaS platform.
Say goodbye to scattered spreadsheets and manual follow-ups. With Ease Margin, your job work management is accurate, traceable, and efficient.`,
  HERO_BUTTON = "Get Started for Free",

  INTRO_TITLE = "🚀 How Ease Margin Simplifies Job Work",

  STEPS_SO_TITLE = "Create Service Orders",
  STEPS_SO_DESC = "Generate professional Service Order Documents instantly. Define requirements clearly and set a solid reference point for both you and your job work partner.",

  STEPS_CHALLAN_TITLE = "Send Materials with Challan",
  STEPS_CHALLAN_DESC = `Easily issue Challans to send raw materials or semi-finished goods to your job worker. Every material movement is logged, ensuring 100% traceability and automatically syncing with your Inventory Management.`,

  STEPS_GRN_TITLE = "Record Goods Received Notes (GRN)",
  STEPS_GRN_DESC = `Once finished goods return, create a GRN to confirm receipt. GRNs automatically match with service orders and challans, ensuring no missing links. This works seamlessly with Purchase Management for consistent handling of supplier and job work receipts.`,

  STEPS_QUALITY_TITLE = "Quality Checks Made Simple",
  STEPS_QUALITY_DESC = "Generate Quality Documents for received materials. Record test results, track supplier performance, and maintain high-quality standards effortlessly.",

  STEPS_INVOICE_TITLE = "Manage Vendor Invoices",
  STEPS_INVOICE_DESC = "Seamlessly record and reconcile invoices from your job work provider. Match invoices against service orders, challans, GRNs, and quality checks for error-free accounting—just like in our Purchase Management module.",

  WHY_TITLE = "💡 Why Choose Ease Margin for Job Work Management?",
  ADVANTAGE_TITLE = "🌟 The Ease Margin Advantage",
  ADVANTAGE_DESC = `Job work management with Ease Margin is not just organized — it’s transparent, reliable, and stress-free.
You stay in control at every stage, while the system ensures efficiency, compliance, and accuracy.
Explore how Job Work integrates with Production Management and Inventory Management to give you complete operational visibility.`,

  CTA_TITLE = "🚀 Ready to streamline your job work process?",
  CTA_BUTTON = "Request a Demo",
}

// --- FEATURE DATA ---
const stepCards: {
  icon: React.ReactNode;
  title: string;
  points: React.ReactNode[];
  bg?: string;
}[] = [
    {
      icon: <SendOutlined />,
      title: JobWorkText.STEPS_CHALLAN_TITLE,
      points: [
        JobWorkText.STEPS_CHALLAN_DESC,
        <>
          Works seamlessly with{" "}
          <Link to="/services/inventory-management">Inventory Management</Link>.
        </>,
      ],
      bg: "#FFF1F0",
    },
    {
      icon: <FileTextOutlined />,
      title: JobWorkText.STEPS_SO_TITLE,
      points: [JobWorkText.STEPS_SO_DESC],
      bg: "#F6FFED",
    },
    {
      icon: <InboxOutlined />,
      title: JobWorkText.STEPS_GRN_TITLE,
      points: [
        JobWorkText.STEPS_GRN_DESC,
        <>
          Aligned with{" "}
          <Link to="/services/purchase-management">Purchase Management</Link>{" "}
          workflows.
        </>,
        // "Aligned with Purchase Management workflows.",
      ],
      bg: "#F9F0FF",
    },
    {
      icon: <ExperimentOutlined />,
      title: JobWorkText.STEPS_QUALITY_TITLE,
      points: [JobWorkText.STEPS_QUALITY_DESC],
      bg: "#E6F7FF",
    },
    {
      icon: <ReconciliationOutlined />,
      title: JobWorkText.STEPS_INVOICE_TITLE,
      points: [
        <>
          Seamlessly record and reconcile invoices from your job work provider.
          Match invoices against service orders, challans, GRNs, and quality
          checks for error-free accounting — just like in our{" "}
          <Link to="/services/purchase-management">Purchase Management</Link>{" "}
          module.
        </>,
      ],
      bg: "#FFFBE6",
    },
  ];

const whyChoose = [
  "End-to-End Control – From service orders to invoices, everything is connected.",
  "Complete Material Traceability – Track every item with challans and GRNs.",
  "Built-in Quality Documentation – Ensure only quality-approved goods re-enter your store.",
  "Accurate Reconciliation – No mismatches, no missed entries.",
  "Time-Saving Automation – Reduce errors and free up your team’s time.",
];

const advantagePoints: React.ReactNode[] = [
  "Job work management with Ease Margin is not just organized — it’s transparent, reliable, and stress-free.",
  "You stay in control at every stage, while the system ensures efficiency, compliance, and accuracy.",
  <>
    Explore how Job Work integrates with{" "}
    <Link to="/services/production-management">Production Management</Link> and{" "}
    <Link to="/services/inventory-management">Inventory Management</Link> to
    give you complete operational visibility.
  </>,
];

const JobWorkManagement: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToElementById("jobwork");
  }, [pathname]);

  return (
    <div id="jobwork">
      <Helmet>
        <title>Job Work Management Software | Ease Margin</title>
        <meta
          name="description"
          content="Manage job work efficiently with Ease Margin. Track materials, service orders, GRNs, and invoices seamlessly with our Job Work Management Software."
        />
        <link
          rel="canonical" href="https://easemargin.com/services/jobwork-management" />
      </Helmet>

      <CustomHeader />
      <Layout className="jobWork-layout">
        {/* Hero (exact same UI layout as previous, new class names) */}
        <Header className="jobWork-header">
          <div className="jobWork-header-inner">
            <Title level={1} className="jobWork-title-large">
              {/* <span role="img" aria-label="wrench">
                🔧
              </span>{" "} */}
              {JobWorkText.HERO_TITLE}
            </Title>
            <Paragraph className="jobWork-paragraph-header">
              {JobWorkText.HERO_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                Request a Demo
              </Button>
            </Link>
          </div>
        </Header>

        <Content className="jobWork-content">
          {/* Intro header */}
          <Title level={3} className="jobWork-title-3rd">
            {JobWorkText.INTRO_TITLE}
          </Title>

          {/* Steps cards */}
          <Row gutter={[24, 24]}>
            {stepCards.map((sec, idx) => (
              <Col xs={24} md={12} key={idx}>
                <Card
                  bordered={false}
                  className="jw-card-default"
                  style={sec.bg ? { backgroundColor: sec.bg } : undefined}
                >
                  <Title level={4} className="jobWork-title-flex">
                    <span style={{ marginRight: 8 }}>{sec.icon}</span>
                    {sec.title}
                  </Title>
                  {sec.points.map((p, i) => (
                    <Paragraph
                      key={i}
                      className="jobWork-paragraph-desc"
                      style={{ marginBottom: 8 }}
                    >
                      <CheckCircleOutlined
                        style={{ color: "#1890ff", marginRight: 8 }}
                      />
                      {p}
                    </Paragraph>
                  ))}
                </Card>
              </Col>
            ))}
          </Row>

          {/* Why choose */}
          <Title level={3} className="jobWork-title-3rd-marginTop">
            {JobWorkText.WHY_TITLE}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
            {whyChoose.map((line, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card
                  bordered={false}
                  className="jw-card-default jw-card-light"
                  style={{ backgroundColor: "#F6FFED" }}
                >
                  <Paragraph className="jobWork-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {line}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <Title level={3} className="jobWork-title-3rd-marginTop ">
            {JobWorkText.ADVANTAGE_TITLE}
          </Title>
          <Card
            bordered={false}
            className="jw-card-default "
            style={{
              marginBottom: 40,
              backgroundColor: "#FFF1F0",
              padding: "10px",
            }}
          >
            {advantagePoints.map((point, i) => (
              <Paragraph key={i} className="jobWork-paragraph-main ">
                <SendOutlined style={{ color: "#1890ff", marginRight: 6 }} />
                {point}
              </Paragraph>
            ))}
          </Card>

          {/* CTA (exact same UI layout as previous, new class names) */}
          <Card
            bordered={false}
            className="jw-card-default jobWork-card-footer"
          >
            <Title level={2} className="jobWork-title-footer">
              {JobWorkText.CTA_TITLE}
            </Title>
            <Paragraph className="jobWork-paragraph-footer">
              <ThunderboltOutlined style={{ marginRight: 8 }} />
              <span>
                Streamline approvals, ensure traceability, and reconcile with
                confidence.
              </span>
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange-jw">
                {JobWorkText.CTA_BUTTON}
              </Button>
            </Link>
          </Card>
        </Content>
      </Layout>
      <Footer />
    </div>
  );
};

export default JobWorkManagement;
